import React, { useEffect } from "react";
import {
	Button,
	Dialog,
	DialogContent,
	DialogActions,
	FormControl,
	Checkbox,
	FormGroup,
	FormControlLabel,
	Typography,
	Grid,
	TextField,
} from "../../../common/MaterialComponent";
import "./RecurringSchedulerDialog.css";
import { useGetComponentStringsContext } from "../../../hooks/useGetComponentStrings";

export interface DialogProps {
	open: boolean;
	confirmDialog: Function;
	cancelDialog: Function;
	isRecurringOption: string;
	recurringDays: number[];
	handleDaySelection: Function;
	index: number;
	dateTime: string | Date;
	recurringSchedulerError: string | null;
	recurringDate: string;
	setRecurringDate: Function;
	isReadOnly: boolean;
	closeDialog: Function;
}

const RecurringSchedulerDialog: React.FC<DialogProps> = (
	props: DialogProps
) => {
	const sharedStrings = useGetComponentStringsContext().Shared;
	const strings = useGetComponentStringsContext().RecurringSchedulerDialog;

	const [weekdaysString, setWeekdaysString] = React.useState("");
	const [datetime, setDatetime] = React.useState(new Date());

	useEffect(() => {
		const buildWeekdaysString = () => {
			let count = 0;
			const noOfDays = props.recurringDays.length;

			let weekdaysSorted = Array.from(props.recurringDays).sort();
			let str = "";
			weekdaysSorted.forEach((day) => {
				switch (day) {
					case 0:
						str += "Monday";
						break;
					case 1:
						str += "Tuesday";
						break;
					case 2:
						str += "Wednesday";
						break;
					case 3:
						str += "Thursday";
						break;
					case 4:
						str += "Friday";
						break;
					case 5:
						str += "Saturday";
						break;
					case 6:
						str += "Sunday";
						break;
				}
				if (count < noOfDays) {
					str += ", ";
				}

				count++;
			});

			if (str === "") {
				str = "N/A";
			}
			setWeekdaysString(str);
		};
		buildWeekdaysString();

		// Check if the datetime is a string and convert to
		// date object if so in order for times/dates to be
		// displayed correctly
		if (typeof props.dateTime == "string") {
			setDatetime(new Date(props.dateTime));
		} else {
			setDatetime(props.dateTime);
		}
	}, [props.recurringDays, props.dateTime]);

	const renderSchedulerView = (index: number) => {
		switch (index) {
			case 0:
				return (
					<Grid item xs={12}>
						<br />
						<Typography variant="subtitle2">
							{`${strings.dailyRecurrencePattern} ${time} ${
								strings.dailyRecurrenceFrom
							} ${datetime.toString().substring(0, 10)}`}
						</Typography>
					</Grid>
				);
			case 1:
				return (
					<>
						<br />
						<br />
						<Typography variant="subtitle2">
							{strings.weeklyRecurrenceInfo}
						</Typography>
						<br />
						<br />
						<FormControl component="fieldset">
							<FormGroup row aria-label="weekdaySelect">
								<FormControlLabel
									value={0}
									control={
										<Checkbox
											color="primary"
											checked={props.recurringDays.includes(0)}
											onChange={() => props.handleDaySelection(0)}
											disabled={props.isReadOnly}
										/>
									}
									label={strings.recurrenceMonday}
									labelPlacement="top"
								/>
								<FormControlLabel
									value={1}
									control={
										<Checkbox
											color="primary"
											checked={props.recurringDays.includes(1)}
											onChange={() => props.handleDaySelection(1)}
											disabled={props.isReadOnly}
										/>
									}
									label={strings.recurrenceTuesday}
									labelPlacement="top"
								/>
								<FormControlLabel
									value={2}
									control={
										<Checkbox
											color="primary"
											checked={props.recurringDays.includes(2)}
											onChange={() => props.handleDaySelection(2)}
											disabled={props.isReadOnly}
										/>
									}
									label={strings.recurrenceWednesday}
									labelPlacement="top"
								/>
								<FormControlLabel
									value={3}
									control={
										<Checkbox
											color="primary"
											checked={props.recurringDays.includes(3)}
											onChange={() => props.handleDaySelection(3)}
											disabled={props.isReadOnly}
										/>
									}
									labelPlacement="top"
									label={strings.recurrenceThursday}
								/>
								<FormControlLabel
									value={4}
									control={
										<Checkbox
											color="primary"
											checked={props.recurringDays.includes(4)}
											onChange={() => props.handleDaySelection(4)}
											disabled={props.isReadOnly}
										/>
									}
									labelPlacement="top"
									label={strings.recurrenceFriday}
								/>
								<FormControlLabel
									value={5}
									control={
										<Checkbox
											color="primary"
											checked={props.recurringDays.includes(5)}
											onChange={() => props.handleDaySelection(5)}
											disabled={props.isReadOnly}
										/>
									}
									labelPlacement="top"
									label={strings.recurrenceSaturday}
								/>
								<FormControlLabel
									value={6}
									control={
										<Checkbox
											color="primary"
											checked={props.recurringDays.includes(6)}
											onChange={() => props.handleDaySelection(6)}
											disabled={props.isReadOnly}
										/>
									}
									labelPlacement="top"
									label={strings.recurrenceSunday}
								/>
							</FormGroup>
						</FormControl>
						<Typography variant="subtitle2">
							{`${strings.weeklyRecurrencePatternStart} ${time} ${
								strings.weeklyRecurrencePatternOn
							} ${weekdaysString} ${
								strings.weeklyRecurrencePatternFrom
							} ${datetime.toString().substring(0, 10)}`}
						</Typography>
					</>
				);
			case 2:
				return (
					<>
						<Typography variant="subtitle2">
							{`${
								strings.monthlyRecurrenceInfo
							} ${datetime.toString().substring(0, 10)} ${
								strings.monthlyRecurrenceAt
							} ${datetime.toString().substr(16, 8)}
						 ${strings.monthlyRecurrenceFrom}`}
						</Typography>
						<TextField
							id="date"
							type="date"
							className="monthlyDatepicker"
							defaultValue={props.recurringDate}
							InputLabelProps={{
								shrink: true,
							}}
							onChange={(event) => props.setRecurringDate(event.target.value)}
							disabled={props.isReadOnly}
						/>
						{parseInt(props.recurringDate.substr(-2)) > 28 && (
							<Typography variant="subtitle2" className="monthlyRangeWarning">
								{strings.monthlyDateRangeWarning}
							</Typography>
						)}
					</>
				);
		}
	};

	const time = datetime.toTimeString().substr(0, 5);
	return (
		<div data-testid="ReccuringScheduleContainer">
			<Dialog open={props.open} fullWidth>
				<DialogContent dividers>
					<Grid container>
						<Grid item xs={12}>
							<Typography variant="h6" className="recurringScheduleTitle">
								{strings.dialogTitle}
							</Typography>
						</Grid>
						{renderSchedulerView(props.index)}
					</Grid>
					<br />
					{props.recurringSchedulerError && (
						<Typography variant="body2" className="errorText">
							{props.recurringSchedulerError}
						</Typography>
					)}
				</DialogContent>
				<DialogActions>
					{props.isReadOnly ? (
						<Button onClick={() => props.closeDialog()} className="confirmBtn">
							{sharedStrings.closeButtonText}
						</Button>
					) : (
						<>
							<Button
								onClick={() => props.confirmDialog()}
								className="confirmBtn"
							>
								{sharedStrings.confirmButtonText}
							</Button>
							<Button
								onClick={() => props.cancelDialog()}
								className="cancelBtn"
							>
								{sharedStrings.cancelButtonText}
							</Button>
						</>
					)}
				</DialogActions>
			</Dialog>
		</div>
	);
};
export default RecurringSchedulerDialog;
